// HubSpot forms are heavy (React, reCAPTCHA, inline styles), so their script is only
// added once a form comes near the screen, or when the popup holding one opens.
// The script renders every form container on the page that is not rendered yet.
const loaded = new Set<string>();

type Tracked = Window & { armadaHubSpot?: () => void };

function inject(src: string): void {
	if (!src || loaded.has(src)) {
		return;
	}

	loaded.add(src);

	// The tracker carries the cookie a submission is tied to, so it goes in with the form
	// rather than waiting for the visitor's first move.
	(window as Tracked).armadaHubSpot?.();

	const script = document.createElement('script');
	script.src = src;
	script.defer = true;
	document.body.appendChild(script);
}

export function loadHubspot(root: ParentNode): void {
	root.querySelectorAll<HTMLElement>('[data-armada-hubspot]').forEach((el) => inject(el.dataset.armadaHubspot || ''));
}

export default function initHubspotLoader(): void {
	// Forms inside a closed dialog never intersect; the popup loads those itself.
	const forms = [...document.querySelectorAll<HTMLElement>('[data-armada-hubspot]')].filter((el) => !el.closest('dialog'));

	if (!forms.length) {
		return;
	}

	if (!('IntersectionObserver' in window)) {
		forms.forEach((el) => inject(el.dataset.armadaHubspot || ''));
		return;
	}

	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					observer.unobserve(entry.target);
					inject((entry.target as HTMLElement).dataset.armadaHubspot || '');
				}
			});
		},
		{ rootMargin: '800px 0px' }
	);

	forms.forEach((el) => observer.observe(el));
}
